import { reactNavHrefForRouteHref } from '@/lib/react-admin-route-map';
import { sectionHash, SETTINGS_SECTION_META } from '@/lib/settings-admin';
import { canViewCompatibilityPolicy } from '@/lib/compatibility-policy-admin';
import { TOOL_REGISTRY } from '@/lib/tools-registry';
import { canAccessEntry } from '@/lib/nav-access';
import { isToolVisibleForUser, type ToolsVisibilityCapabilities } from '@/lib/tools-visibility';
import type { AuthUser, PermissionCatalogEntry } from '@/lib/types';
import type { QuickSearchRegistryResult } from './quick-search-types';

/** Manual enrichments only — aliases, keywords, labels. Permissions come from catalog access helpers. */
const PAGE_REGISTRY_EXTRAS: Record<
  string,
  { aliases?: string[]; keywords?: string[]; subtitle?: string }
> = {
  'reports-admin': { aliases: ['incidents'], keywords: ['incident', 'report', 'multimedia'] },
  'sites-admin': { aliases: ['dealers', 'dealer tree'], keywords: ['site', 'dealer', 'location'] },
  contacts: { keywords: ['contact', 'recipient', 'phone', 'email'] },
  'import-wizard': { aliases: ['wizard', 'import'], keywords: ['onboard', 'setup'] },
  sops: { aliases: ['sop', 'standard operating procedure'], keywords: ['workflow', 'procedure'] },
  'operator-simulator': { aliases: ['simulator'], keywords: ['operator', 'simulate', 'test'] },
  'report-delivery': { aliases: ['delivery rules', 'event rules'], keywords: ['rule', 'delivery', 'schedule'] },
  templates: { keywords: ['template', 'email', 'sms', 'wrapper'] },
  settings: { aliases: ['config', 'configuration'], keywords: ['system settings'] },
  tools: { keywords: ['diagnostics', 'audit', 'utility'] },
  'retention-hub': { keywords: ['retention', 'cleanup'] },
  stats: { keywords: ['statistics', 'metrics'] },
  users: { keywords: ['user', 'account management'] },
  roles: { keywords: ['role', 'permission'] },
};

function normalizeHref(href: string): string {
  return reactNavHrefForRouteHref(href.replace(/^\/+/, ''));
}

function pageRegistryEntry(entry: PermissionCatalogEntry): QuickSearchRegistryResult {
  const extras = PAGE_REGISTRY_EXTRAS[entry.id] ?? {};
  const keywords = [
    entry.label,
    entry.description ?? '',
    ...(extras.aliases ?? []),
    ...(extras.keywords ?? []),
  ]
    .filter(Boolean)
    .map((value) => value.toLowerCase());

  return {
    id: `page:${entry.id}`,
    kind: 'page',
    type: 'page',
    title: entry.label,
    subtitle: extras.subtitle ?? entry.description,
    group: 'Pages',
    href: normalizeHref(entry.href),
    source: 'registry',
    keywords,
  };
}

const TOOL_SEARCH_EXTRAS: Record<string, string[]> = {
  'configuration-validation': [
    'validation check',
    'validation checks',
    'configuration validation',
    'config validation',
    'validation run',
    'validation findings',
  ],
};

function toolRegistryEntry(tool: (typeof TOOL_REGISTRY)[number]): QuickSearchRegistryResult {
  const categoryLabel = tool.category.replace(/-/g, ' ');
  const extras = TOOL_SEARCH_EXTRAS[tool.id] ?? [];
  return {
    id: `tool:${tool.id}`,
    kind: 'page',
    type: 'tool',
    title: tool.title,
    subtitle: tool.description,
    group: 'Tools',
    href: `/tools.html?tool=${encodeURIComponent(tool.id)}`,
    source: 'registry',
    keywords: [
      tool.title,
      tool.shortTitle ?? '',
      tool.description,
      tool.id,
      categoryLabel,
      'tool',
      'tools',
      ...extras,
    ]
      .filter(Boolean)
      .map((value) => value.toLowerCase()),
  };
}

function configRegistryEntry(section: (typeof SETTINGS_SECTION_META)[number]): QuickSearchRegistryResult {
  const hash = sectionHash(section.id);
  return {
    id: `config:${section.id}`,
    kind: 'page',
    type: 'config',
    title: section.label,
    subtitle: section.group,
    group: 'Configuration',
    href: `/settings.html#${hash}`,
    source: 'registry',
    keywords: [section.label, section.group, section.description, 'settings', 'config', section.id]
      .filter(Boolean)
      .map((value) => value.toLowerCase()),
  };
}

export function buildQuickSearchRegistry(input: {
  navEntries: PermissionCatalogEntry[];
  user: AuthUser | null;
  toolsCapabilities: ToolsVisibilityCapabilities;
}): QuickSearchRegistryResult[] {
  const results: QuickSearchRegistryResult[] = [];

  for (const entry of input.navEntries) {
    if (!canAccessEntry(entry, input.user)) continue;
    results.push(pageRegistryEntry(entry));
  }

  const toolsPageVisible = input.navEntries.some(
    (entry) => entry.id === 'tools' && canAccessEntry(entry, input.user),
  );
  if (toolsPageVisible) {
    for (const tool of TOOL_REGISTRY) {
      if (!isToolVisibleForUser(tool, input.toolsCapabilities)) continue;
      results.push(toolRegistryEntry(tool));
    }
  }

  const settingsVisible = input.navEntries.some(
    (entry) => entry.id === 'settings' && canAccessEntry(entry, input.user),
  );
  if (settingsVisible) {
    for (const section of SETTINGS_SECTION_META) {
      if (section.id === 'compatibility_policy' && !canViewCompatibilityPolicy(input.user)) {
        continue;
      }
      results.push(configRegistryEntry(section));
    }
  }

  return results;
}

/** Top destinations for empty-query hints (permission-filtered). */
export function pickQuickSearchHighlights(
  registry: QuickSearchRegistryResult[],
  limit = 6,
): QuickSearchRegistryResult[] {
  const preferredIds = new Set([
    'page:reports-admin',
    'page:sites-admin',
    'page:contacts',
    'page:sops',
    'page:tools',
    'page:settings',
  ]);
  const preferred = registry.filter((entry) => preferredIds.has(entry.id));
  if (preferred.length >= limit) return preferred.slice(0, limit);
  const rest = registry.filter((entry) => !preferredIds.has(entry.id));
  return [...preferred, ...rest].slice(0, limit);
}

export function filterQuickSearchRegistry(
  registry: QuickSearchRegistryResult[],
  query: string,
): QuickSearchRegistryResult[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return registry;
  return registry.filter((entry) =>
    entry.keywords.some((keyword) => keyword.includes(normalized)) ||
    entry.title.toLowerCase().includes(normalized) ||
    (entry.subtitle?.toLowerCase().includes(normalized) ?? false),
  );
}
